import React, { useEffect, useState, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useToast } from 'use-toast-mui';
import { useQuery } from '@tanstack/react-query';
import EmployeeGuideModal from './EmployeeGuideModal';
import QueryString from 'qs';
import * as XLSX from 'xlsx';
import AnimationPage from '../../../pages/AnimationPage';
import { getAllStaffs } from '../../../api/staff';
import { getEmployeePositionsList } from '../../../api/employeePosition';
import { getSalaryEmployees } from '../../../api/salarySetting';
import useUserAuth from '../../../utils/useUserAuth';
import EmployeePosition from '../EmployeePosition';
import EmployeeBranchManagement from '../EmployeeBranchManagement/EmployeeBranchManagement';
import EmployeeSettingDashboard from './EmployeeSettingDashboard';
import EmployeeSettingTable from './EmployeeSettingTable';
import { PageContainer } from './EmployeeSettingStyled';

const EmployeeSetting = () => {
  const { t } = useTranslation();
  const toast = useToast();
  const user = useSelector(state => state.user.user);
  const canCreate = useUserAuth('EMPLOYEE_CREATE');
  const token = user?.token;
  const userBranch = user?.branch;
  const userHead = user?.headBranch;

  const [opens, setOpens] = useState({
    openPosition: false,
    openBranchManagement: false,
  });
  const [guideOpen, setGuideOpen] = useState(false);

  const [query, setQuery] = useState(() => {
    const saved = sessionStorage.getItem('employeeSearchConditions');
    if (saved) {
      sessionStorage.removeItem('employeeSearchConditions');
      try { return JSON.parse(saved); } catch { /* fallthrough */ }
    }
    return {
      page: 1,
      limit: 10,
      headBranchId: userHead?.headBranchId,
      branchId: userBranch?.branchId,
      searchInput: '',
      responsibilities: [],
      classifications: ['WORKING'],
    };
  });

  // 필터 상태
  // 토글 UI는 sessionStorage에서 복원된 query.classifications와 항상 일치해야 한다.
  // 무조건 true로 초기화하면, 퇴사자 제외를 끈 채로 상세화면에 들어갔다 목록으로 돌아왔을 때
  // 토글은 켜진 것처럼 보이는데 실제 필터(query)는 꺼진 상태로 남는 불일치가 생긴다.
  const [excludeLeft, setExcludeLeft] = useState(() => (query.classifications?.length ?? 0) > 0);
  const [positionFilter, setPositionFilter] = useState('');
  const [classificationFilter, setClassificationFilter] = useState('');
  const [salaryTypeFilter, setSalaryTypeFilter] = useState('');
  const [sortField, setSortField] = useState('hiredDate');
  const [sortDirection, setSortDirection] = useState('DESC');

  // user 변경 시 query 업데이트
  useEffect(() => {
    setQuery(prev => ({
      ...prev,
      headBranchId: userHead?.headBranchId,
      branchId: userBranch?.branchId,
    }));
  }, [userHead?.headBranchId, userBranch?.branchId]);

  const handleExcludeLeftChange = checked => {
    setExcludeLeft(checked);
    setQuery(prev => ({
      ...prev,
      classifications: checked ? ['WORKING'] : [],
      page: 1,
    }));
  };

  // 임직원 목록 조회 (React Query)
  const queryString = QueryString.stringify(query);
  const hasBranch = !!userHead?.headBranchId && (user.centerType !== 'BRANCH' || !!userBranch?.branchId);

  const { data: employeesRaw, refetch, isFetching } = useQuery({
    queryKey: ['employees', queryString],
    queryFn: () => getAllStaffs(token, queryString),
    enabled: !!token && hasBranch,
    placeholderData: prev => prev,
  });

  // 직급 목록 조회
  const positionQueryString = QueryString.stringify({
    headBranchId: userHead?.headBranchId,
    branchId: userBranch?.branchId,
  });

  const { data: positionsRaw } = useQuery({
    queryKey: ['employeePositions', userHead?.headBranchId, userBranch?.branchId],
    queryFn: () => getEmployeePositionsList(token, positionQueryString),
    enabled: !!token && !!userHead?.headBranchId,
  });

  const positionOptions = useMemo(() => {
    if (!positionsRaw) return [];
    const list = Array.isArray(positionsRaw) ? positionsRaw : positionsRaw?.content || [];
    return [...list]
      .sort((a, b) => (a.employeePositionOrder || 999) - (b.employeePositionOrder || 999))
      .map(p => ({
        value: p.positionName || p.employeePositionName || p.name || '',
        label: p.positionName || p.employeePositionName || p.name || '',
      })).filter(p => p.value);
  }, [positionsRaw]);

  // 대시보드 통계 계산 (전체 목록 기준)
  const statsQueryString = QueryString.stringify({
    page: 1,
    limit: 9999,
    headBranchId: userHead?.headBranchId,
    branchId: userBranch?.branchId,
  });

  const { data: allEmployees } = useQuery({
    queryKey: ['employeesStats', userHead?.headBranchId, userBranch?.branchId],
    queryFn: () => getAllStaffs(token, statsQueryString),
    enabled: !!token && hasBranch,
  });

  // 급여유형별 카운트 (급여설정 직원 API)
  const { data: salaryEmployeeGroups = [] } = useQuery({
    queryKey: ['salaryEmployeesForStats', userBranch?.branchId],
    queryFn: () => getSalaryEmployees(userBranch?.branchId, false, token),
    enabled: !!userBranch?.branchId && !!token,
  });

  const { salaryTypeCounts, salaryTypeMap } = useMemo(() => {
    const counts = { COUNT: 0, MONTHLY: 0, HOURLY: 0 };
    const map = {};
    salaryEmployeeGroups.forEach(g => {
      (g.employees || []).forEach(e => {
        if (e.salaryType) {
          if (counts[e.salaryType] !== undefined) counts[e.salaryType]++;
          map[e.employeeId] = e.salaryType;
        }
      });
    });
    return { salaryTypeCounts: counts, salaryTypeMap: map };
  }, [salaryEmployeeGroups]);

  // 프론트 필터링 (직급, 급여유형) + salaryType 주입
  const employees = useMemo(() => {
    if (!employeesRaw) return employeesRaw;
    let filtered = (employeesRaw.content || []).map(e => ({
      ...e,
      salaryType: salaryTypeMap[e.id] || null,
    }));

    if (positionFilter) {
      filtered = filtered.filter(e => e.empoloyeePositionName === positionFilter);
    }

    if (filtered.length === (employeesRaw.content || []).length) return { ...employeesRaw, content: filtered };
    return { ...employeesRaw, content: filtered, totalElements: filtered.length };
  }, [employeesRaw, positionFilter, salaryTypeMap]);

  const stats = useMemo(() => {
    const list = allEmployees?.content || [];
    const working = list.filter(e => e.classification === 'WORKING');
    const left = list.filter(e => e.classification === 'LEFT');

    const countResponsibility = key => list.filter(e =>
      e.classification === 'WORKING' && e.responsibilities?.includes(key)
    ).length;

    return {
      total: list.length,
      counting: salaryTypeCounts.COUNT,
      monthly: salaryTypeCounts.MONTHLY,
      hourly: salaryTypeCounts.HOURLY,
      working: working.length,
      left: left.length,
      privateLessonCount: countResponsibility('PRIVATE_LESSON'),
      groupLessonCount: countResponsibility('GROUP_LESSON'),
      customerManagementCount: countResponsibility('CUSTOMER_MANAGEMENT'),
      storeManagementCount: countResponsibility('STORE_MANAGEMENT'),
    };
  }, [allEmployees, salaryTypeCounts]);

  // 핸들러
  const handleSearch = searchValue => {
    setQuery(prev => ({ ...prev, searchInput: searchValue, page: 1 }));
  };

  const handlePageChange = (_, value) => {
    setQuery(prev => ({ ...prev, page: value }));
  };

  const handleLimitChange = e => {
    setQuery(prev => ({ ...prev, limit: e.target.value, page: 1 }));
  };

  const handlePositionFilterChange = value => {
    setPositionFilter(value);
  };

  const handleSalaryTypeFilterChange = value => {
    setSalaryTypeFilter(value);
  };

  const RESPONSIBILITY_LABELS = {
    PRIVATE_LESSON: '개인레슨',
    GROUP_LESSON: '그룹레슨',
    ONLINE_LESSON: '온라인레슨',
    CUSTOMER_MANAGEMENT: '고객관리',
    STORE_MANAGEMENT: '매장관리',
  };

  const handleExcelDownload = () => {
    const list = employees?.content;
    if (!list || list.length === 0) {
      toast.warning('다운로드할 데이터가 없습니다');
      return;
    }

    const data = list.map(emp => ({
      '입사일': emp.hiredDate || '',
      '이름': emp.name || '',
      '직급': emp.empoloyeePositionName || '',
      '성별': emp.gender === 'FEMALE' ? '여성' : '남성',
      '전화번호': emp.phoneNumber || '',
      '담당업무': (emp.responsibilities || []).map(r => RESPONSIBILITY_LABELS[r] || r).join(', '),
      '출입가능 구역': (emp.accessGroupNames || []).join(', '),
      '재직구분': emp.classification === 'WORKING' ? '재직중' : '퇴사',
      '앱노출': emp.exposureYn === 'Y' ? '노출' : '비노출',
    }));

    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet(data);
    XLSX.utils.book_append_sheet(wb, ws, '임직원목록');
    XLSX.writeFile(wb, `임직원목록_${new Date().toISOString().slice(0, 10)}.xlsx`);
    toast.success('엑셀 다운로드가 완료되었습니다');
  };

  const handleSortChange = (field, direction) => {
    setSortField(field || '');
    setSortDirection(direction || '');
    const updated = { ...query, page: 1 };
    if (field) {
      updated.sortField = field;
      updated.sortDirection = direction;
    } else {
      delete updated.sortField;
      delete updated.sortDirection;
    }
    setQuery(updated);
  };

  const saveSearchConditions = () => {
    sessionStorage.setItem('employeeSearchConditions', JSON.stringify(query));
  };

  return (
    <AnimationPage>
      <PageContainer>
        <EmployeeSettingDashboard
          stats={stats}
          canCreate={canCreate}
          onPositionManage={() => setOpens(prev => ({ ...prev, openPosition: true }))}
          onAddExistingEmployee={() => setOpens(prev => ({ ...prev, openBranchManagement: true }))}
        />

        <EmployeeSettingTable
          employees={employees}
          query={query}
          onSearch={handleSearch}
          onPageChange={handlePageChange}
          onLimitChange={handleLimitChange}
          excludeLeft={excludeLeft}
          onExcludeLeftChange={handleExcludeLeftChange}
          positionFilter={positionFilter}
          onPositionFilterChange={handlePositionFilterChange}
          positionOptions={positionOptions}
          salaryTypeFilter={salaryTypeFilter}
          onSalaryTypeFilterChange={handleSalaryTypeFilterChange}
          loading={isFetching}
          onExcelDownload={handleExcelDownload}
          sortField={sortField}
          sortDirection={sortDirection}
          onSortChange={handleSortChange}
          onRowClick={saveSearchConditions}
          onGuideOpen={() => setGuideOpen(true)}
        />

        {/* Position Management Modal */}
        <EmployeePosition setOpens={setOpens} opens={opens} onUpdate={refetch} />

        {/* Branch Management Modal */}
        <EmployeeBranchManagement
          open={opens.openBranchManagement}
          onClose={() => {
            setOpens(prev => ({ ...prev, openBranchManagement: false }));
            refetch();
          }}
        />
        <EmployeeGuideModal open={guideOpen} onClose={() => setGuideOpen(false)} />
      </PageContainer>
    </AnimationPage>
  );
};

export default EmployeeSetting;
